import React from 'react'
import { colors, borderRadius } from '../../styles/tokens'
import { formatFullDate, formatPrice, formatPassengers } from '../../utils/formatters'
import BlockPreview from './BlockPreview'

/**
 * Preview completo da cotação com confirmação
 * 
 * Single Responsibility: Exibir preview e permitir confirmação
 * 
 * NOTA: Campo de correção por IA foi movido para o QuoteEditor (pós-publicação).
 * Props correction/setCorrection/refining/onCorrection mantidas por contrato
 * mas não renderizam UI — falseia para não quebrar nenhum import.
 */
/**
 * Uma ocorrência da extração — o que o código determinístico decidiu.
 *
 * Três níveis, e a diferença entre eles é só uma: quanto do tempo do
 * vendedor custam.
 *
 *   info      — o sistema corrigiu sozinho. Zero clique. Só informa.
 *   alerta    — o sistema NÃO alterou porque não tem autoridade para isso
 *               (ano escrito pelo vendedor, ano futuro sem respaldo na
 *               fonte). Zero clique obrigatório. Visível.
 *   pergunta  — o sistema não consegue derivar o fato. Bloqueia.
 *
 * Numa cotação sem ambiguidade a lista é vazia e esta seção não renderiza:
 * o vendedor vê exatamente a tela que via antes.
 */
function Ocorrencia({ issue }) {
  const estilo = {
    info:     { fundo: 'rgba(59,130,246,0.08)',  borda: '#3b82f6', icone: 'ℹ️' },
    alerta:   { fundo: 'rgba(245,158,11,0.10)',  borda: '#f59e0b', icone: '⚠️' },
    pergunta: { fundo: 'rgba(239,68,68,0.10)',   borda: '#ef4444', icone: '❓' },
  }[issue.nivel] || { fundo: 'transparent', borda: colors.border, icone: '•' }

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
      padding: '0.6rem 0.75rem', marginTop: '0.5rem',
      backgroundColor: estilo.fundo,
      borderLeft: `3px solid ${estilo.borda}`,
      borderRadius: borderRadius.sm || '4px',
      fontSize: '0.85rem', color: colors.text.primary,
    }}>
      <span>{estilo.icone}</span>
      <span>{issue.mensagem}</span>
    </div>
  )
}

export default function AssistantPreview({
  preview,
  clientName,
  setClientName,
  correction,       // mantido por contrato (não usado)
  setCorrection,    // mantido por contrato (não usado)
  refining,         // mantido por contrato (não usado)
  loading,
  onCorrection,     // mantido por contrato (não usado)
  onConfirm,
  onReset,
  issues = []
}) {
  const passengersText = formatPassengers(
    preview?.adults,
    preview?.children,
    preview?.infants
  )
  const perguntasPendentes = issues.filter(i => i.nivel === 'pergunta')
  const bloqueado = loading || !clientName.trim() || perguntasPendentes.length > 0

  return (
    <div style={{
      padding: '1.5rem',
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: 'rgba(16, 185, 129, 0.05)'
    }}>
      <p style={{ margin: '0 0 1rem', color: colors.text.primary, fontWeight: 500 }}>
        ✅ Pronto! Confira os detalhes da cotação:
      </p>

      <div style={{
        backgroundColor: colors.card,
        borderRadius: borderRadius.md,
        padding: '1.5rem',
        border: `1px solid ${colors.border}`
      }}>
        {/* Cabeçalho */}
        <div style={{
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: `1px solid ${colors.border}`
        }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.text.primary }}>
            📍 {preview.destination || 'Destino'}
          </div>
          <div style={{ fontSize: '0.9rem', color: colors.text.secondary, marginTop: '0.5rem' }}>
            📅 {formatFullDate(preview.start_date)} - {formatFullDate(preview.end_date)}
            {' • '}
            👥 {passengersText}
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.primary, marginTop: '0.5rem' }}>
            💰 {formatPrice(preview.total_price)}
          </div>
        </div>

        {/* Blocos detalhados */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {(preview.blocks || []).map((block, i) => (
            <BlockPreview key={i} block={block} />
          ))}
        </div>
      </div>



      {/* Ocorrências da extração — ausentes no caso normal */}
      {issues.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          {issues.map((issue, i) => <Ocorrencia key={i} issue={issue} />)}
        </div>
      )}

      {/* Botões de ação */}
      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={onConfirm}
          disabled={bloqueado}
          style={{
            flex: 1,
            padding: '0.875rem 1.5rem',
            backgroundColor: bloqueado ? '#ccc' : '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: borderRadius.md,
            fontWeight: 500,
            fontSize: '1rem',
            cursor: bloqueado ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? '⏳ Verificando imagens...' : '✅ Confirmar e criar cotação'}
        </button>
        <button
          onClick={onReset}
          disabled={loading}
          style={{
            padding: '0.875rem 1.5rem',
            backgroundColor: 'transparent',
            color: colors.text.secondary,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.md,
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          🔄 Recomeçar
        </button>
      </div>
    </div>
  )
}
